import {AbstractControl, FormArray, FormGroup, ValidatorFn} from '@angular/forms';

/**
 * One server-side validation failure as returned by the Forms service's `422`
 * (`SubmissionValidator.ValidationError` → `{ "errors": [ { fieldKey, code, message } ] }`).
 */
export interface SubmissionFieldError {
  /** The offending field's key. Empty for a form-level (cross-field) failure that belongs on no single control. */
  fieldKey: string;
  /** Machine code, e.g. `required`, `email`, `pattern`, `crossField`. */
  code: string;
  /** Localised message the server already produced (custom per-field message when the form defines one). */
  message: string;
}

/** The Angular error key a server-reported failure is stamped under, so the renderer can show it verbatim. */
export const SERVER_ERROR_KEY = 'serverError';

/**
 * Pulls the per-field payload out of an `HttpErrorResponse` for a `422`. Returns an empty list for any other
 * failure (or a `422` whose body doesn't carry the expected shape), so callers can fall back to a generic banner.
 *
 * Before #258 the completion screen threw this payload away and showed one generic sentence, which made a
 * validation mismatch unrecoverable: the employee was told "something is wrong" with nothing marked.
 */
export function readSubmissionFieldErrors(error: unknown): SubmissionFieldError[] {
  const candidate = (error as { status?: number; error?: { errors?: unknown } } | null | undefined);
  if (!candidate || candidate.status !== 422) return [];

  const errors = candidate.error?.errors;
  if (!Array.isArray(errors)) return [];

  return errors
    .filter((e): e is SubmissionFieldError =>
      !!e && typeof e === 'object' && typeof (e as SubmissionFieldError).message === 'string')
    .map(e => ({fieldKey: e.fieldKey ?? '', code: e.code ?? '', message: e.message}));
}

/** The server-error validator currently attached to a control, so a later stamp replaces rather than stacks. */
const stamped = new WeakMap<AbstractControl, ValidatorFn>();

/**
 * Stamps server-reported failures onto the matching controls so the offending field is marked and shows its own
 * message, and returns the ones that belong to no control (form-level / unknown key) for a banner.
 *
 * Controls are marked touched because the renderer only shows errors on a touched/dirty control.
 */
export function applyServerFieldErrors(form: FormGroup, errors: SubmissionFieldError[]): SubmissionFieldError[] {
  const unmatched: SubmissionFieldError[] = [];

  for (const error of errors) {
    const control = error.fieldKey ? findControl(form, error.fieldKey) : null;
    if (!control) {
      unmatched.push(error);
      continue;
    }

    stampServerError(control, error.message);
  }

  return unmatched;
}

/**
 * Attaches the server's verdict as a real **validator** rather than a one-off `setErrors`.
 *
 * `setErrors` looks simpler but does not survive: Angular's `formControl` directive re-validates the control when
 * its view is (re-)initialised, which silently wipes a directly-written error — and that is exactly what happens
 * here, because surfacing a 422 also moves the wizard off the review step onto the offending page, re-creating
 * those very controls. As a validator the message survives the re-render, and it still self-clears: the moment
 * the value differs from the one the server rejected, the validator returns null.
 */
function stampServerError(control: AbstractControl, message: string): void {
  const previous = stamped.get(control);
  if (previous) control.removeValidators(previous);

  const rejected = identity(control.value);
  const validator: ValidatorFn = c => identity(c.value) === rejected ? {[SERVER_ERROR_KEY]: message} : null;

  stamped.set(control, validator);
  control.addValidators(validator);
  control.updateValueAndValidity({emitEvent: false});
  control.markAsTouched();
}

/** Stable identity of a control value, so array-valued fields (multiselect, checkbox-group) compare correctly. */
function identity(value: unknown): string {
  return JSON.stringify(value ?? null);
}

/** A repeating-row error key as reported by the server, e.g. `nebenjobs[1].nb_arbeitgeber` (#259). */
const ROW_KEY = /^(.+)\[(\d+)]\.(.+)$/;

/**
 * Finds a control by its **leaf** field key anywhere in the tree. The Forms service stores and reports fields
 * flat by their (globally unique) key, while the runtime form nests them under group keys — so a plain
 * `form.get(key)` misses anything inside a group.
 *
 * A key qualified with a row index (`listKey[2].fieldKey`) resolves to that exact row, so an error in the third
 * repeating entry marks the third entry — not the first one that happens to share the field key.
 */
export function findControl(form: FormGroup, fieldKey: string): AbstractControl | null {
  const row = ROW_KEY.exec(fieldKey);
  if (row) {
    const [, listKey, index, leaf] = row;
    const array = findControl(form, listKey);
    if (array instanceof FormArray) {
      const item = array.at(Number(index));
      if (item instanceof FormGroup) return findControl(item, leaf);
    }
    return null;
  }

  const direct = form.get(fieldKey);
  if (direct) return direct;

  for (const child of Object.values(form.controls)) {
    if (child instanceof FormGroup) {
      const found = findControl(child, fieldKey);
      if (found) return found;
    } else if (child instanceof FormArray) {
      for (const item of child.controls) {
        if (item instanceof FormGroup) {
          const found = findControl(item, fieldKey);
          if (found) return found;
        }
      }
    }
  }

  return null;
}
